import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LockClosedIcon, HomeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const Forbidden = () => {
    const { refreshUser } = useAuth();
    const navigate = useNavigate();
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefreshPermissions = async () => {
        setIsRefreshing(true);
        try {
            const result = await refreshUser();
            if (result.success && result.user) {
                const perms = result.user.permissions;
                const permsArr = Array.isArray(perms) ? perms : [];
                const isSuper = perms === 'all';
                const hasViewPerm = isSuper || permsArr.some(p => p.endsWith('.view') || p.includes('view'));

                if (hasViewPerm) {
                    toast.success('Permissions refreshed! Redirecting...');
                    const targetPage = isSuper || permsArr.includes('dashboard.view')
                        ? '/dashboard'
                        : (permsArr.includes('leads.view') ? '/leads' : '/profile');
                    setTimeout(() => {
                        navigate(targetPage, { replace: true });
                    }, 500);
                } else {
                    toast.error('Permission update required. Please contact administrator.');
                }
            } else {
                toast.error('Failed to refresh permissions. Please try logging out and logging back in.');
            }
        } catch (error) {
            console.error('Error refreshing permissions:', error);
            toast.error('Failed to refresh permissions. Please try logging out and logging back in.');
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="text-center px-4">
                <LockClosedIcon className="h-24 w-24 text-gray-400 mx-auto mb-6" />
                <h1 className="text-6xl font-bold text-gray-900 mb-4">403</h1>
                <h2 className="text-2xl font-semibold text-gray-700 mb-2">Access Denied</h2>
                <p className="text-gray-500 mb-8 max-w-md mx-auto">
                    You don't have permission to access this page. If you were just granted permissions, try refreshing them below.
                </p>
                <div className="flex items-center justify-center gap-4">
                    <button
                        onClick={handleRefreshPermissions}
                        disabled={isRefreshing}
                        className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ArrowPathIcon className={`h-5 w-5 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                        {isRefreshing ? 'Refreshing...' : 'Refresh Permissions'}
                    </button>
                    <Link
                        to="/dashboard"
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <HomeIcon className="h-5 w-5 mr-2" />
                        Go to Dashboard
                    </Link>
                </div>
                <p className="text-sm text-gray-400 mt-4">
                    If refreshing doesn't work, please contact your administrator or try logging out and logging back in.
                </p>
            </div>
        </div>
    );
};

export default Forbidden;

